import { useState } from "react";

import { Client } from "@/Common/Api/Client";
import { LoadPatientModel } from "@/Common/Api/Patient/Dto/LoadPatientModel";
import { useMount } from "@/Common/CustomHooks/useMount";
import { Patient } from "@/Common/Models/Patient/Patient";
import { Person } from "@/Common/Models/Person/Person";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { PatientMergeRequestCancellationConfirmationPopup } from "./PatientMergeRequestCancellationConfirmationPopup";

interface PatientMergeRequestModel {
    Id: string;
    MergeToPatientContactId: string;
    MergeFromPatientContactId: string;
}

async function getPatientMergeRequestAssociatedWithCurrentPatient(contactId: string): Promise<{ patientMergeRequest: PatientMergeRequestModel, otherPatientPersonModel: Patient }> {
    const response = await Client.postAsync<{ PatientMergeRequest: PatientMergeRequestModel, OtherPatient: LoadPatientModel }>(ServerOperations.Server.Myle_Patient_GetCurrentMergeRequest, { PatientContactId: contactId });
    if (!response.isSuccess) {
        sessionStore.showPopup(response.errors);
        return;
    }

    return {
        patientMergeRequest: response.data.PatientMergeRequest,
        otherPatientPersonModel: Patient.fromResponseData(response.data.OtherPatient)
    };
}

async function cancelPatientMergeRequest(patientMergeRequestId: string, then: () => void): Promise<void> {
    const response = await Client.postAsync(ServerOperations.Server.Myle_Patient_CancelMergeRequest, { PatientMergeRequestId: patientMergeRequestId });
    if (!response.isSuccess) {
        sessionStore.showPopup(response.errors);
        return;
    }

    then();
}

function PatientMergeRequestCancellationController({ currentPatient, onClose }: Readonly<{ currentPatient: Patient, onClose: () => void }>): JSX.Element {
    const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
    const [patients, setPatients] = useState<{ patientA: Person, patientB: Person, mergeRequest: PatientMergeRequestModel }>(null);

    useMount(
        () => {
            async function getMergeRequest(): Promise<void> {
                try {
                    const response = await getPatientMergeRequestAssociatedWithCurrentPatient(currentPatient.IndividualResponse.Contact_id);
                    if (!response)
                        return;

                    let currentPatientPerson = Person.FromPatient(currentPatient);
                    let otherPatientPerson = Person.FromPatient(response.otherPatientPersonModel);

                    if (response.patientMergeRequest.MergeToPatientContactId === currentPatientPerson.ContactId && response.patientMergeRequest.MergeFromPatientContactId === otherPatientPerson.ContactId) {
                        setPatients({ patientA: currentPatientPerson, patientB: otherPatientPerson, mergeRequest: response.patientMergeRequest });
                        setShowConfirmationPopup(true);
                    }
                    else if (response.patientMergeRequest.MergeFromPatientContactId === currentPatientPerson.ContactId && response.patientMergeRequest.MergeToPatientContactId === otherPatientPerson.ContactId) {
                        setPatients({ patientA: otherPatientPerson, patientB: currentPatientPerson, mergeRequest: response.patientMergeRequest });
                        setShowConfirmationPopup(true);
                    }
                }
                catch (e) {
                    console.log(e);
                }
            }
            getMergeRequest();
        }
    );

    if (!showConfirmationPopup || !patients)
        return null;

    return (
        <PatientMergeRequestCancellationConfirmationPopup
            patientA={patients.patientA}
            patientB={patients.patientB}
            onConfirm={() => cancelPatientMergeRequest(patients.mergeRequest.Id, () => {
                sessionStore.dispatch(LocalPatientOperations.Profile.UpdateCancelPatientMergeStatus, { mergeToPatientContactId: patients.patientA.ContactId, mergeFromPatientContactId: patients.patientB.ContactId });
                onClose();
            })}
            onClose={onClose}
        />
    );
}

export { PatientMergeRequestCancellationController };
